import gql from 'graphql-tag';

import { Query, Mutation } from 'core';

const CARD_FIELDS = gql`
  fragment cardFields on CreditCard {
    id
    stripeId
    brand
    country
    name
    expMonth
    expYear
    lastDigits
    isDefault
  }
`;

const SEATS_USAGE = gql`
  fragment seatsUsage on SeatsUsage {
    id
    usedSpaceSeats
    usedProjectsSeats
    usedInvitesSeats
    unusedSeats
    includedPlanSeats
    purchasedSeats
    freeSeats
    totalSubSeats
    seatsChange
  }
`;

const SUBSCRIPTION = gql`
  ${SEATS_USAGE}
  fragment subscription on BillingSubscription {
    id
    scaSecret
    currentPeriodEnd
    nextAmount
    cancelAtPeriodEnd
    discount
    credits
    plan {
      id
      name
    }
    seats {
      ...seatsUsage
    }
    status
  }
`;

const SUBSCRIPTION_PREVIEW = gql`
  ${SEATS_USAGE}
  fragment subscriptionPreview on SubscriptionPreview {
    id
    totalAmount
    perSeatAmount
    initialAmount
    billingStarts
    prorationAmount
    prorationTimestamp
    discountAmount
    seats {
      ...seatsUsage
    }
  }
`;

const PROFILE_SUBSCRIPTION = gql`
  ${CARD_FIELDS}
  ${SUBSCRIPTION}
  ${SEATS_USAGE}
  query ProfileSubscription($spaceSlug: String!) {
    profile(username: $spaceSlug) {
      id
      username
      countryCode
      taxId
      hasValidPaymentMethod
      hasProSubscription
      inSpace {
        id
        trialPeriodEnd
        flags
      }
      billingSubscriptions {
        ...subscription
        plan {
          isLegacy
        }
      }
      creditCards {
        ...cardFields
      }

      seatsUsage {
        ...seatsUsage
      }
      isEarlyAccess
      __typename
    }
  }
`;

export class ProfileSubscriptionQuery extends Query {
  static conf = {
    GQL: PROFILE_SUBSCRIPTION,
    varNames: ['spaceSlug', 'coupon', 'extraSeats'],
    resultPath: 'profile',
  };
}

const INITIATIVE_STORAGE_USAGE = gql`
  query StorageUsage($spaceSlug: String) {
    initiative(slug: $spaceSlug) {
      storageUsage {
        usedSize
      }
    }
  }
`;

const PROFILE_STORAGE_USAGE = gql`
  query StorageUsage($username: String!) {
    profile(username: $username) {
      storageUsage {
        usedSize
      }
    }
  }
`;

const PROFILE_CONVERSION_USAGE = gql`
  query ConversionUsage($username: String!) {
    profile(username: $username) {
      conversionUsage {
        usedConversionCount
      }
    }
  }
`;

const INITIATIVE_PREVIEW_SUBSCRIPTION = gql`
  ${CARD_FIELDS}
  ${SUBSCRIPTION}
  ${SEATS_USAGE}

  query InitiativeSubscriptions($spaceSlug: String) {
    initiative(slug: $spaceSlug) {
      id
      slug
      countryCode
      taxId
      hasValidPaymentMethod
      hasProSubscription
      inSpace {
        id
        trialPeriodEnd
        flags
      }
      billingSubscriptions {
        ...subscription
      }
      creditCards {
        ...cardFields
      }

      seatsUsage {
        ...seatsUsage
      }
      isEarlyAccess
      __typename
    }
  }
`;

export class ProfileStorageUsageQuery extends Query {
  static conf = {
    GQL: PROFILE_STORAGE_USAGE,
    varNames: ['username'],
    resultPath: 'profile',
  };
}

export class ProfileConversionUsageQuery extends Query {
  static conf = {
    GQL: PROFILE_CONVERSION_USAGE,
    varNames: ['username'],
    resultPath: 'profile',
  };
}

export class InitiativeStorageUsageQuery extends Query {
  static conf = {
    GQL: INITIATIVE_STORAGE_USAGE,
    varNames: ['spaceSlug'],
    resultPath: 'initiative',
  };
}
export class InitiativePreviewSubscriptionQuery extends Query {
  static conf = {
    GQL: INITIATIVE_PREVIEW_SUBSCRIPTION,
    varNames: ['spaceSlug', 'coupon', 'extraSeats'],
    resultPath: 'initiative',
  };
}

const CREATE_SUBSCRIPTION_MUTATION = gql`
  mutation NewSubscription($subscriptionInput: BillingSubscribeInput) {
    billingSubscribe(subscriptionData: $subscriptionInput) {
      subscription {
        id
        scaSecret
        nextAmount
      }
    }
  }
`;

export class CreateSubscriptionMutation extends Mutation {
  static conf = {
    GQL: CREATE_SUBSCRIPTION_MUTATION,
    inputName: 'subscriptionInput',
    resultPath: 'billingSubscribe.subscription',
  };
}

const CANCEL_SUBSCRIPTION_MUTATION = gql`
  mutation CancelSubscription(
    $cancelSubscriptionInput: BillingCancelSubscriptionInput
  ) {
    billingCancel(subscriptionData: $cancelSubscriptionInput) {
      subscription {
        id
        status
      }
    }
  }
`;

const REACTIVATE_SUBSCRIPTION_MUTATION = gql`
  mutation reactivateSubscription($arguments: ReactivateSubscriptionInput) {
    reactivateSubscription(arguments: $arguments) {
      subscription {
        id
        planId
        spaceId
        status
        cancelAtPeriodEnd
        currentPeriodStart
        currentPeriodEnd
        nextAmount
        trialEnd
        scaSecret
        purchasedSeats
        SeatsIncluded
        plan {
          name
          product {
            name
          }
        }
      }
    }
  }
`;

export class ReactivateSubscriptionMutation extends Mutation {
  static conf = {
    GQL: REACTIVATE_SUBSCRIPTION_MUTATION,
    inputName: 'arguments',
    resultPath: 'reactivateSubscription.subscription',
  };
}

export class CancelSubscriptionMutation extends Mutation {
  static conf = {
    GQL: CANCEL_SUBSCRIPTION_MUTATION,
    inputName: 'cancelSubscriptionInput',
    // resultPath: 'billingSubscribe.subscription',
  };
}

const PROFILE_CREDIT_CARDS = gql`
  ${CARD_FIELDS}
  query ProfileCreditCards($spaceSlug: String!) {
    profile(username: $spaceSlug) {
      id
      username
      hasValidPaymentMethod
      creditCards {
        ...cardFields
      }
      __typename
    }
  }
`;

export class ProfileCreditCardsQuery extends Query {
  static conf = {
    GQL: PROFILE_CREDIT_CARDS,
    varNames: ['spaceSlug'],
    resultPath: 'profile',
  };
}

const INITIATIVE_CREDIT_CARDS = gql`
  ${CARD_FIELDS}
  query InitiativeCreditCards($spaceSlug: String) {
    initiative(slug: $spaceSlug) {
      id
      slug
      hasValidPaymentMethod
      creditCards {
        ...cardFields
      }
      __typename
    }
  }
`;

export class InitiativeCreditCardsQuery extends Query {
  static conf = {
    GQL: INITIATIVE_CREDIT_CARDS,
    varNames: ['spaceSlug'],
    resultPath: 'initiative',
  };
}

const PROFILE_INVOICES = gql`
  query ProfileInvoices($spaceSlug: String!) {
    profile(username: $spaceSlug) {
      id
      username
      invoices {
        id
        status
        amount
        periodStart
        periodEnd
        number
        paymentMethod
        downloadUrl
      }
      __typename
    }
  }
`;

export class ProfileInvoicesQuery extends Query {
  static conf = {
    GQL: PROFILE_INVOICES,
    varNames: ['spaceSlug'],
    resultPath: 'profile',
  };
}

const INITIATIVE_INVOICES = gql`
  query InitiativeInvoices($spaceSlug: String) {
    initiative(slug: $spaceSlug) {
      id
      slug
      invoices {
        id
        status
        number
        periodStart
        periodEnd
        paymentMethod
        amount
        downloadUrl
      }
      __typename
    }
  }
`;

export class InitiativeInvoicesQuery extends Query {
  static conf = {
    GQL: INITIATIVE_INVOICES,
    varNames: ['spaceSlug'],
    resultPath: 'initiative',
  };
}

export const ADD_PAYMENT_METHOD_MUTATION = gql`
  ${CARD_FIELDS}
  mutation AddPaymentMethod($paymentMethodData: AddPaymentMethodInput) {
    addPaymentMethod(paymentMethodData: $paymentMethodData) {
      paymentMethod {
        ...cardFields
      }
    }
  }
`;

export class AddPaymentMethodMutation extends Mutation {
  static conf = {
    GQL: ADD_PAYMENT_METHOD_MUTATION,
    inputName: 'paymentMethodData',
    resultPath: 'addPaymentMethod.paymentMethod',
  };
}

export const DEFAULT_PAYMENT_METHOD_MUTATION = gql`
  mutation MakeDefaulPaymentMethodMutation(
    $paymentMethodData: MakeDefaultPaymentMethodInput
  ) {
    makeDefaultPaymentMethod(paymentMethodData: $paymentMethodData) {
      paymentMethod {
        id
      }
    }
  }
`;

export class MakeDefaulPaymentMethodMutation extends Mutation {
  static conf = {
    GQL: DEFAULT_PAYMENT_METHOD_MUTATION,
    inputName: 'paymentMethodData',
  };
}

export const DELETE_PAYMENT_METHOD_MUTATION = gql`
  mutation DeletePaymentMutation($paymentMethodData: DeletePaymentMethodInput) {
    deletePaymentMethod(paymentMethodData: $paymentMethodData) {
      success
    }
  }
`;

export class DeletePaymentMethodMutaton extends Mutation {
  static conf = {
    GQL: DELETE_PAYMENT_METHOD_MUTATION,
    inputName: 'paymentMethodData',
  };
}

export const ADD_SUBSCRIPTION_SEATS_MUTATION = gql`
  ${SUBSCRIPTION}
  mutation AddSubscriptionSeatsMutation($arguments: BillingAddSeatsInput) {
    billingAddSeats(arguments: $arguments) {
      subscription {
        ...subscription
      }
    }
  }
`;

export class AddSubscriptionSeatsMutation extends Mutation {
  static conf = {
    GQL: ADD_SUBSCRIPTION_SEATS_MUTATION,
    inputName: 'arguments',
  };
}

export const REMOVE_SUBSCRIPTION_SEATS_MUTATION = gql`
  ${SUBSCRIPTION}
  mutation RemoveSubscriptionSeatsMutation(
    $arguments: BillingRemoveSeatsInput
  ) {
    billingRemoveSeats(arguments: $arguments) {
      subscription {
        ...subscription
      }
    }
  }
`;

export class RemoveSubscriptionSeatsMutation extends Mutation {
  static conf = {
    GQL: REMOVE_SUBSCRIPTION_SEATS_MUTATION,
    inputName: 'arguments',
  };
}

const PROFILE_ALTER_SEATS_PREVIEW = gql`
  ${SUBSCRIPTION}
  ${SUBSCRIPTION_PREVIEW}
  query ProfileSeatsPreview(
    $spaceSlug: String!
    $addedSeats: Int!
    $removedSeats: Int!
  ) {
    profile(username: $spaceSlug) {
      id
      username
      billingSubscriptions {
        id
        ...subscription
        addSeatsPreview(nSeats: $addedSeats) {
          ...subscriptionPreview
        }
        removeSeatsPreview(nSeats: $removedSeats) {
          ...subscriptionPreview
        }
      }
      __typename
    }
  }
`;

export class ProfileAlterSeatsPreviewQuery extends Query {
  static conf = {
    GQL: PROFILE_ALTER_SEATS_PREVIEW,
    varNames: ['spaceSlug', 'addedSeats', 'removedSeats'],
    resultPath: 'profile',
  };
}

const INITIATIVE_ALTER_SEATS_PREVIEW = gql`
  ${SUBSCRIPTION}
  ${SUBSCRIPTION_PREVIEW}
  query InitiativeSeatsPreview(
    $spaceSlug: String
    $addedSeats: Int!
    $removedSeats: Int!
  ) {
    initiative(slug: $spaceSlug) {
      id
      slug
      billingSubscriptions {
        id
        ...subscription
        addSeatsPreview(nSeats: $addedSeats) {
          ...subscriptionPreview
        }
        removeSeatsPreview(nSeats: $removedSeats) {
          ...subscriptionPreview
        }
      }
      __typename
    }
  }
`;

export class OrganizationAlterSeatsPreviewQuery extends Query {
  static conf = {
    GQL: INITIATIVE_ALTER_SEATS_PREVIEW,
    varNames: ['spaceSlug', 'addedSeats', 'removedSeats'],
    resultPath: 'initiative',
  };
}
